'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@ps/ui/utils';

import { useVideoPlayerContext } from '../contexts/video-player';

export function VideoSeekbar({ className }: { className?: string }) {
  const { currentTime$, totalDuration$, videoRef$, positionMs$ } =
    useVideoPlayerContext();

  const [sliderValue, setSliderValue] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [previewTime, setPreviewTime] = useState<number | null>(null);
  const [previewPosition, setPreviewPosition] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timeSub = currentTime$.subscribe(val => {
      if (!isSeeking) setSliderValue(val);
    });
    const durSub = totalDuration$.subscribe(setDuration);
    return () => {
      timeSub.unsubscribe();
      durSub.unsubscribe();
    };
  }, [currentTime$, totalDuration$, isSeeking]);

  const seekTo = useCallback(
    (time: number) => {
      const videoRef = videoRef$.getValue();
      const el = videoRef?.current;
      if (el) {
        el.currentTime = time;
        positionMs$.next(time * 1000);
        currentTime$.next(time);
      }
    },
    [videoRef$, positionMs$, currentTime$],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || duration === 0) return;
      const percentage = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      const time = percentage * duration;
      setSliderValue(time);
      seekTo(time);
    },
    [duration, seekTo],
  );

  const handleMouseDown = useCallback(() => {
    setIsSeeking(true);
    setShowPreview(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsSeeking(false);
  }, []);

  const extractFrame = useCallback(
    (timeInSeconds: number) => {
      const videoRef = videoRef$.getValue();
      const el = videoRef?.current;
      if (!el) return;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = 160;
        canvas.height = 90;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Create a temporary video for frame extraction
        const tempVideo = document.createElement('video');
        tempVideo.crossOrigin = 'anonymous';
        tempVideo.src = el.src;
        tempVideo.currentTime = timeInSeconds;
        tempVideo.addEventListener(
          'seeked',
          () => {
            ctx.drawImage(tempVideo, 0, 0, 160, 90);
            setPreviewImage(canvas.toDataURL('image/jpeg', 0.5));
            setShowPreview(true);
            tempVideo.remove();
          },
          { once: true },
        );
      } catch {
        // Frame extraction may fail with CORS
      }
    },
    [videoRef$],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isSeeking) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || duration === 0) return;

      const percentage = Math.max(
        0,
        Math.min(1, (e.clientX - rect.left) / rect.width),
      );
      const timeAtPosition = percentage * duration;

      setPreviewPosition(percentage * 100);
      setPreviewTime(timeAtPosition);

      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      previewTimeoutRef.current = setTimeout(() => {
        extractFrame(timeAtPosition);
      }, 100);
    },
    [duration, extractFrame, isSeeking],
  );

  const handleMouseLeave = useCallback(() => {
    setShowPreview(false);
    setPreviewImage(null);
    setPreviewTime(null);
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
  }, []);

  if (duration === 0) return null;

  const progress = duration > 0 ? sliderValue / duration : 0;

  return (
    <div
      ref={containerRef}
      className={cn('relative flex cursor-pointer items-center', className)}
      style={{ width: '120%', height: 4, justifyContent: 'center' }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Preview thumbnail */}
      {showPreview && previewImage && previewTime !== null && (
        <div
          className="absolute bottom-6 -translate-x-1/2 overflow-hidden rounded border border-white/30 shadow-lg"
          style={{ left: `${previewPosition}%` }}
        >
          <img
            src={previewImage}
            alt="Preview"
            className="h-[90px] w-[160px] object-cover"
          />
          <div className="bg-black/70 py-0.5 text-center text-[10px] text-white">
            {formatSeekTime(previewTime)}
          </div>
        </div>
      )}

      {/* Track background */}
      <div className="absolute inset-0 bg-white/20" />

      {/* Progress bar with gradient */}
      <div
        className="absolute left-0 top-0 h-full"
        style={{
          width: `${progress * 100}%`,
          background: 'linear-gradient(to right, #FF0000, #FF00FF)',
        }}
      />

      <div
        className="pointer-events-none absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF00FF] shadow"
        style={{ left: `${progress * 100}%` }}
      />

      {isSeeking && (
        <div
          className="pointer-events-none absolute bottom-4 -translate-x-1/2 rounded bg-black/70 px-2 py-1 text-xs font-medium tabular-nums text-white"
          style={{ left: `clamp(24px, ${progress * 100}%, calc(100% - 24px))` }}
        >
          {formatSeekTime(sliderValue)}
        </div>
      )}

      {/* Invisible slider for interaction */}
      <input
        type="range"
        className="absolute -inset-y-2 inset-x-0 cursor-pointer opacity-0"
        min={0}
        max={duration || 1}
        step={0.01}
        value={sliderValue}
        onChange={e => {
          const val = parseFloat(e.target.value);
          setSliderValue(val);
          seekTo(val);
        }}
        onPointerDown={() => setIsSeeking(true)}
        onPointerUp={() => setIsSeeking(false)}
        onPointerCancel={() => setIsSeeking(false)}
        onBlur={() => setIsSeeking(false)}
      />
    </div>
  );
}

function formatSeekTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
